import { useEffect, useMemo, useRef, useState } from 'react';
import { BRAND } from './lib/brand';
import { loadCourse } from './lib/courses';
import { formatKm, loadDirectory, searchDirectory, STATE_NAMES, type Directory, type SearchHit } from './lib/directory';
import { useGps } from './lib/gps';
import { useSettings } from './lib/settings';
import { distanceM } from './lib/geo';
import { initialState, setManual, step as detectStep, type DetectorState } from './lib/holeDetector';
import { pushYardage, widgetSupported } from './lib/yardageWidget';
import type { Course } from './lib/types';

/**
 * One page: find a course, hand it to the widgets, get out of the way.
 *
 * The dashboard tile is the product — this exists only to tell it which course
 * it is looking at. Everything the full app does (live map, hole detection,
 * voice, routing) still lives in the project at /full; this is the surface the
 * customer's golfers actually touch, and it is deliberately one screen.
 */
export default function SearchApp() {
  const { settings, update } = useSettings();
  const { fix } = useGps();
  const [dir, setDir] = useState<Directory | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [course, setCourse] = useState<Course | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadDirectory().then(setDir).catch(() => {});
  }, []);

  const hits = useMemo(() => {
    if (!dir) return [];
    const near = fix ? fix.pos : null;
    return query.trim().length >= 2
      ? searchDirectory(dir, { query, near, limit: 12 })
      : near
        ? searchDirectory(dir, { near, limit: 6 })
        : [];
  }, [dir, query, fix]);

  /**
   * Which hole the golfer is on.
   *
   * Not "nearest green": holes run alongside each other, so a plain nearest
   * test flips between 2 and 7 as you walk down a shared fairway and the tile
   * jitters between them. The detector is sequence-aware — it knows you came
   * from hole 1, that hole 2 is the likely next one, and it holds a hole until
   * the evidence for another one is sustained rather than momentary.
   */
  const detector = useRef<DetectorState>(initialState(1));
  // Mirrored into state so the screen re-renders when the hole changes; the ref
  // alone would leave the displayed number a fix behind.
  const [holeNo, setHoleNo] = useState(1);
  /**
   * How sure the detector is, mirrored for display.
   *
   * The arrows are always usable, but the label has to be honest about where
   * the number came from: "HOLE 4" while the phone is still on the couch is a
   * claim the app cannot back up, and a golfer who trusts it once and finds it
   * wrong stops trusting the yardage too.
   */
  const [confidence, setConfidence] = useState(0);

  /**
   * Step the hole by hand.
   *
   * Exists so the whole pipeline — detector, publish, widget redraw — can be
   * tested from a driveway. Waiting until you are physically on hole 2 to find
   * out whether hole 2 works is a bad way to find a bug. It uses the detector's
   * own manual hold, so GPS resumes control after a few minutes rather than
   * being overridden for the rest of the round.
   */
  const stepHole = (delta: number) => {
    if (!course) return;
    const i = course.holes.findIndex((h) => h.number === holeNo);
    const next = course.holes[Math.max(0, Math.min(course.holes.length - 1, i + delta))];
    if (!next) return;
    detector.current = setManual(detector.current, next.number, Date.now());
    publish(course);
  };

  const publish = (c: Course) => {
    if (!c.holes.length) return;
    if (fix && !fix.stale) {
      detector.current = detectStep(detector.current, {
        course: c,
        pos: fix.pos,
        accuracy: fix.accuracy,
        teeColour: settings.teeColour,
        now: fix.ts,
      });
    }
    const h =
      c.holes.find((x) => x.number === detector.current.hole) ?? c.holes[0];
    if (!h) return;
    setHoleNo(h.number);
    setConfidence(fix && !fix.stale ? detector.current.confidence : 0);
    const from = fix?.pos ?? h.tees[settings.teeColour] ?? h.tees.White;
    const tee = h.tees[settings.teeColour] ?? h.tees.White;
    void pushYardage({
      course: c.name,
      hole: h.number,
      par: h.par,
      metres: distanceM(from, h.green.middle),
      unit: settings.unit,
      ...(fix ? { playerLat: fix.pos[1], playerLng: fix.pos[0] } : {}),
      teeLat: tee[1], teeLng: tee[0],
      greenLat: h.green.middle[1], greenLng: h.green.middle[0],
    });
  };

  const choose = async (hit: SearchHit) => {
    setBusy(true);
    setError(null);
    setQuery('');
    update({ courseId: hit.id, courseName: hit.name });
    detector.current = initialState(1);
    setHoleNo(1);
    const { course: c, failure } = await loadCourse(hit.id);
    setBusy(false);
    if (!c.holes.length) {
      setError(
        failure === 'unavailable'
          ? 'Could not reach the course data. Try again in a moment.'
          : 'This course has no holes mapped yet. Pick another.'
      );
      return;
    }
    setCourse(c);
    publish(c);
  };

  // Reload the saved course on launch so a docked phone re-arms the widget
  // without anyone touching it.
  useEffect(() => {
    const id = settings.courseId;
    if (!id || course) return;
    setBusy(true);
    loadCourse(id).then(({ course: c }) => {
      setBusy(false);
      if (c.holes.length) {
        setCourse(c);
        publish(c);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the tile current while this screen is open and the phone is moving.
  useEffect(() => {
    if (course && fix) publish(course);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fix, course]);

  // A ticker as well as the fix handler. Fixes stop arriving when the golfer
  // stands still, and pushYardage's heartbeat can only fire if something calls
  // it — without this a tile goes stale on a tee while somebody waits to play.
  useEffect(() => {
    if (!course) return;
    const id = window.setInterval(() => publish(course), 20_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  return (
    <div className="searchapp">
      <div className="sa-brand">{BRAND.name}</div>

      <input
        ref={input}
        className="sa-input"
        value={query}
        placeholder={dir ? `Search ${dir.count.toLocaleString()} courses` : 'Loading courses…'}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
        autoCorrect="off"
      />

      {course && !query && (
        <div className="sa-active">
          <div className="sa-active-label">SENDING TO YOUR DASHBOARD</div>
          <div className="sa-active-name">{course.name}</div>
          <div className="sa-active-meta">
            {course.holes.length} holes ·{' '}
            {fix ? `GPS ±${Math.round(fix.accuracy)}m` : 'waiting for GPS'}
          </div>
          <div className={`sa-holestep ${confidence >= 0.35 ? 'live' : ''}`}>
            <button onClick={() => stepHole(-1)} disabled={holeNo <= course.holes[0].number}>‹</button>
            <span>
              HOLE {holeNo}
              <em>
                {!fix
                  ? 'waiting for GPS'
                  : confidence >= 0.35
                    ? 'detected'
                    : 'not on a hole yet · set it here'}
              </em>
            </span>
            <button
              onClick={() => stepHole(1)}
              disabled={holeNo >= course.holes[course.holes.length - 1].number}
            >
              ›
            </button>
          </div>
        </div>
      )}

      {busy && <div className="sa-note">Loading the course…</div>}
      {error && <div className="sa-note sa-error">{error}</div>}

      {!!hits.length && (
        <div className="sa-list">
          {!query && <div className="sa-label">NEAREST TO YOU</div>}
          {hits.map((c) => (
            <button key={c.id} className="sa-row" onClick={() => void choose(c)}>
              <span className="sa-row-name">{c.name}</span>
              <span className="sa-row-meta">
                {[c.suburb, STATE_NAMES[c.state] ?? c.state].filter(Boolean).join(' · ')}
                {c.distanceM != null ? ` · ${formatKm(c.distanceM)}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {!widgetSupported() && (
        <div className="sa-note">Widgets are iOS only — this page still picks the course.</div>
      )}

    </div>
  );
}
